/**
 * Boomfection Landing — Recent Games Module
 * Recent games list and game-of-the-day highlight
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 *
 * NOTE: innerHTML usage below is safe — all user-provided strings are sanitized
 * via escapeHtml(), and numeric values are coerced via parseInt(). This is an
 * existing pattern moved verbatim from the monolithic landing.js (pure refactor).
 */

import { escapeHtml } from '/js/ui/utils.js';
import { t } from '/js/i18n.js';
import { safeJsonFetch } from './LandingUtils.js';

/**
 * Returns a human-readable relative time string
 * @param {string} dateStr - ISO date string
 * @returns {string}
 */
export function timeAgo(dateStr) {
    const tSafe = (key, fallback, params) => {
        const v = t(key, params);
        return v === key ? fallback : v;
    };
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffSec = Math.floor((now - then) / 1000);

    if (diffSec < 60) return tSafe('time.justNow', 'gerade eben');
    const minutes = Math.floor(diffSec / 60);
    if (diffSec < 3600) return tSafe('time.minutesAgo', `vor ${minutes} Min`, { n: minutes });
    const hours = Math.floor(diffSec / 3600);
    if (diffSec < 86400) return tSafe('time.hoursAgo', `vor ${hours} Std`, { n: hours });
    const days = Math.floor(diffSec / 86400);
    return tSafe('time.daysAgo', `vor ${days} Tag${days > 1 ? 'en' : ''}`, { n: days });
}

/**
 * Loads the recent games list into the landing page.
 * All user-facing strings are sanitized via escapeHtml; numeric values are integers from API.
 */
export async function loadRecentGames() {
    const container = document.getElementById('recent-games');
    if (!container) return;

    try {
        const data = await safeJsonFetch('/api/games/recent?limit=5');
        if (!data || !data.games || data.games.length === 0) {
            container.innerHTML = `<div class="games-empty">${t('game.noRecentGames')}</div>`;
            return;
        }

        // Safe: all dynamic values are either escapeHtml'd or parseInt'd
        container.innerHTML = data.games
            .map(function (g) {
                const replayLink =
                    g.hasReplay && g.gameId
                        ? `<button class="btn-replay-watch" data-game-id="${escapeHtml(g.gameId)}" title="${t('game.replayTitle')}">&#x25B6; ${t('game.replay')}</button>`
                        : '';

                return (
                    `<div class="recent-game-row">` +
                    `<div class="recent-game-winner">` +
                    `<span class="recent-game-icon">&#x1F3C6;</span>` +
                    `<span class="recent-game-name">${escapeHtml(g.winner)}</span>` +
                    `</div>` +
                    `<div class="recent-game-details">` +
                    `<span class="recent-game-map">${escapeHtml(g.mapSizeName)}</span>` +
                    `<span class="recent-game-points">${parseInt(g.points) || 0} ${t('game.pointsShort')}</span>` +
                    `<span class="recent-game-chain">${t('game.chainCount', { n: parseInt(g.longestChain) || 0 })}</span>` +
                    `<span class="recent-game-time">${timeAgo(g.createdAt)}</span>${replayLink}</div>` +
                    `</div>`
                );
            })
            .join('');
    } catch (err) {
        console.error('[Landing] Failed to load recent games:', err);
    }
}

/**
 * Initialisiert Replay-Watch-Buttons (Click-Handler via Event-Delegation)
 * @param {Function} onReplayRequest - Callback(gameId) wenn User Replay starten will
 */
export function initReplayButtons(onReplayRequest) {
    document.addEventListener('click', (e) => {
        const btn = e.target.closest('.btn-replay-watch');
        if (!btn) return;
        const gameId = btn.dataset.gameId;
        if (gameId && onReplayRequest) {
            onReplayRequest(gameId);
        }
    });
}

/**
 * Loads the game-of-the-day highlight card
 */
export async function loadGameHighlight() {
    const card = document.getElementById('highlight-card');
    if (!card) return;

    try {
        const data = await safeJsonFetch('/api/games/highlight');
        if (!data || data.none) {
            card.classList.add('hidden');
            return;
        }

        const highlightPlayer = document.getElementById('highlight-player');
        const highlightStat = document.getElementById('highlight-stat');
        const highlightMeta = document.getElementById('highlight-meta');
        if (!highlightPlayer || !highlightStat || !highlightMeta) return;
        highlightPlayer.textContent = data.winner;
        highlightStat.textContent = t('game.chainReaction', { n: data.longestChain });
        highlightMeta.textContent = `${data.mapSizeName} | ${data.points} ${t('game.pointsFull')}`;
        card.classList.remove('hidden');
    } catch (err) {
        console.error('[Landing] Failed to load game highlight:', err);
        card.classList.add('hidden');
    }
}
